'use client';

import {isBrowser} from '@/lib/utils/page.utils';
import {useEffect, useLayoutEffect, useState} from 'react';
import {UAParser} from 'ua-parser-js';
/**
 * Returns true if user agent is determined to be firefox
 */
export const useIsFirefox = () => {
	const [isFirefox, setIsFirefox] = useState(false);
	const [isFirefoxDefined, setIsFirefoxDefined] = useState(false);

	useEffect(() => {
		if (isBrowser()) {
			const parser = new UAParser();
			const browser = parser.getBrowser();
			const firefox = Boolean((browser.name || '').toLowerCase().includes('firefox'));
			setIsFirefox(firefox);
			setIsFirefoxDefined(true);
		}
	}, []);
	return {isFirefox, isFirefoxDefined};
};

export const useIsomorphicLayoutEffect = isBrowser() ? useLayoutEffect : useEffect;

type UseMediaQueryOptions = {
	defaultValue?: boolean;
	initializeWithValue?: boolean;
};

export enum MediaType {
	sm = '(max-width: 767px)',
	md = '(min-width: 768px) and (max-width: 1023px)',
	mdDown = '(max-width: 1023px)',
	lg = '(min-width: 1024px)',
	lgDown = '(max-width: 1024px)'
}

const IS_SERVER = typeof window === 'undefined';

export function useMediaQuery(
	type: keyof typeof MediaType | MediaType,
	{defaultValue = false, initializeWithValue = true}: UseMediaQueryOptions = {}
): boolean {
	const query: string = MediaType[type] || type;

	const getMatches = (query: string): boolean => {
		if (IS_SERVER) {
			return defaultValue;
		}
		return window.matchMedia(query).matches;
	};

	const [matches, setMatches] = useState<boolean>(() => {
		if (initializeWithValue) {
			return getMatches(query);
		}
		return defaultValue;
	});

	// Handles the change event of the media query.
	function handleChange() {
		setMatches(getMatches(query));
	}

	useIsomorphicLayoutEffect(() => {
		const matchMedia = window.matchMedia(query);

		// Triggered at the first client-side load and if query changes
		handleChange();

		// Use deprecated `addListener` and `removeListener` to support Safari < 14 (#135)
		if (matchMedia.addListener) {
			matchMedia.addListener(handleChange);
		} else {
			matchMedia.addEventListener('change', handleChange);
		}

		return () => {
			if (matchMedia.removeListener) {
				matchMedia.removeListener(handleChange);
			} else {
				matchMedia.removeEventListener('change', handleChange);
			}
		};
	}, [query]);

	return matches;
}

export function useNetworkStatus(): boolean {
	const [isOnline, setIsOnline] = useState(() => navigator.onLine);

	useEffect(() => {
		const handleOnline = () => setIsOnline(true);
		const handleOffline = () => setIsOnline(false);

		window.addEventListener('online', handleOnline);
		window.addEventListener('offline', handleOffline);

		return () => {
			window.removeEventListener('online', handleOnline);
			window.removeEventListener('offline', handleOffline);
		};
	}, []);

	return isOnline;
}

export function useIsIOSDevice() {
	const [isIOS, setIsIOS] = useState(false);

	useEffect(() => {
		const ua = navigator.userAgent;
		const maxTouchPoints = navigator.maxTouchPoints || 0;

		// Detect iPhone, iPad (including iPadOS 13+ - reports as Macintosh)
		const ios = /iPad|iPhone/.test(ua) || (maxTouchPoints > 1 && /Macintosh/.test(ua));

		setIsIOS(ios);
	}, []);

	return isIOS;
}
